import React, { useCallback, useMemo, useState } from 'react'
import { Badge, message, Switch } from 'antd'
import API from 'utils/api'
import { useToken } from 'app/hooks'
import useServiceUsers from 'app/gomsacademy/hooks/ServiceUsers'
import {
	Card,
	EmptyState,
	Footer,
	Header,
	PageWrapper,
	PaginationControls,
	SearchForm,
	ServiceCell,
	StatusBadge,
	TableCard,
	TableWrapper,
	UsersTable,
} from './styles'

type ServiceKey = 'goms' | 'want'

interface PendingToggle {
	userId: number
	serviceKey: ServiceKey
}

export const ServiceManagement: React.FC = () => {
	const PAGE_SIZE = 20
	const { data: token } = useToken()
	const [offset, setOffset] = useState(0)
	const [searchInput, setSearchInput] = useState('')
	const [activeSearch, setActiveSearch] = useState('')
	const [typeInput, setTypeInput] = useState<string>('')
	const [activeType, setActiveType] = useState<number | null>(null)
	const [groupIdInput, setGroupIdInput] = useState<string>('')
	const [activeGroupId, setActiveGroupId] = useState<number | null>(null)
	const [approvalFilter, setApprovalFilter] = useState<'all' | 'pending'>('all')
	const [pendingToggle, setPendingToggle] = useState<PendingToggle | null>(null)
	const [pendingApproveDeptId, setPendingApproveDeptId] = useState<number | null>(null)
	const [pendingRejectDeptId, setPendingRejectDeptId] = useState<number | null>(null)

	const { data, isLoading, isValidating, mutate, error } = useServiceUsers({
		limit: PAGE_SIZE,
		offset,
		search: activeSearch || undefined,
		type: activeType,
		groupId: activeGroupId,
		approvalStatus: approvalFilter === 'pending' ? 'pending' : null,
	})

	const total = data?.total ?? 0
	const limit = data?.limit ?? PAGE_SIZE
	const currentOffset = data?.offset ?? offset
	const currentPage = limit ? Math.floor(currentOffset / limit) + 1 : 1
	const totalPages = limit ? Math.max(1, Math.ceil(total / limit)) : 1
	const items = data?.items ?? []

	const handleSearchSubmit = useCallback(
		(event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault()
			setOffset(0)
			setActiveSearch(searchInput.trim())
			const typeVal = typeInput.trim()
			const parsedType = typeVal ? Number(typeVal) : NaN
			setActiveType(!typeVal || Number.isNaN(parsedType) ? null : parsedType)
			const gidVal = groupIdInput.trim()
			const parsedGroupId = gidVal ? Number(gidVal) : NaN
			setActiveGroupId(!gidVal || Number.isNaN(parsedGroupId) ? null : parsedGroupId)
		},
		[searchInput, typeInput, groupIdInput]
	)

	const handleClearSearch = useCallback(() => {
		setSearchInput('')
		setActiveSearch('')
		setTypeInput('')
		setActiveType(null)
		setGroupIdInput('')
		setActiveGroupId(null)
		setApprovalFilter('all')
		setOffset(0)
	}, [])

	const goToPreviousPage = useCallback(() => {
		setOffset(prev => Math.max(0, prev - limit))
	}, [limit])

	const goToNextPage = useCallback(() => {
		setOffset(prev => prev + limit)
	}, [limit])

	const isPaginatingForwardDisabled = useMemo(() => currentOffset + limit >= total, [currentOffset, limit, total])
	const isPaginatingBackwardDisabled = useMemo(() => currentOffset === 0, [currentOffset])

	const handleToggle = useCallback(
		async (userId: number, serviceKey: ServiceKey, nextValue: boolean) => {
			if (!token) {
				message.error('인증 정보가 없습니다. 다시 로그인해 주세요.')
				return
			}

			setPendingToggle({ userId, serviceKey })

			await mutate(
				current => {
					if (!current) return current
					return {
						...current,
						items: current.items.map(item =>
							item.id === userId
								? {
									...item,
									services: {
										...item.services,
										[serviceKey]: nextValue,
									},
								}
								: item
						),
					}
				},
				{ revalidate: false }
			)

			try {
				const response = await API.put(`/admin/services/users/${userId}`, token + '', {
					serviceKey,
					isActive: nextValue,
				})

				const successMessage =
					response?.result?.message ||
					`${serviceKey === 'goms' ? 'GOMS' : 'WANT'
					} 서비스가 ${nextValue ? '활성화' : '비활성화'}되었습니다.`
				message.success(successMessage)
			} catch (err: any) {
				console.error(err)
				message.error(err?.message || '서비스 상태 변경에 실패했습니다.')
			} finally {
				setPendingToggle(null)
				await mutate()
			}
		},
		[mutate, token]
	)

	const renderServiceSwitch = useCallback(
		(userId: number, serviceKey: ServiceKey, checked: boolean) => {
			const isCurrentPending =
				pendingToggle?.userId === userId && pendingToggle?.serviceKey === serviceKey

			const label = checked ? '활성' : '비활성'

			return (
				<ServiceCell>
					<Switch
						size="small"
						checked={checked}
						onChange={value => handleToggle(userId, serviceKey, value)}
						loading={isCurrentPending}
						disabled={!!pendingToggle && !isCurrentPending}
					/>
					<span className="switch-label">{label}</span>
				</ServiceCell>
			)
		},
		[handleToggle, pendingToggle]
	)

	const hasActiveService = useCallback(
		(services?: { goms: boolean; want: boolean }) => !!services && (services.goms || services.want),
		[]
	)

	const isPendingApproval = useCallback(
		(approvalStatus?: string) =>
			approvalStatus === 'pending' || approvalStatus === 'none',
		[]
	)

	const handleApproveAcademy = useCallback(
		async (deptId: number) => {
			if (!token) {
				message.error('인증 정보가 없습니다.')
				return
			}
			setPendingApproveDeptId(deptId)
			try {
				const res = await API.post(
					`/admin/services/registrations/${deptId}/approve`,
					token + '',
					{}
				)
				if (res?.result?.success) {
					message.success(res.result.message || '학원 가입이 승인되었습니다.')
					await mutate()
				} else {
					message.error(res?.message || '승인에 실패했습니다.')
				}
			} catch (err: any) {
				message.error(err?.message || '승인 요청에 실패했습니다.')
			} finally {
				setPendingApproveDeptId(null)
			}
		},
		[token, mutate]
	)

	const handleRejectAcademy = useCallback(
		async (deptId: number, reason?: string) => {
			if (!token) {
				message.error('인증 정보가 없습니다.')
				return
			}
			setPendingRejectDeptId(deptId)
			try {
				const res = await API.post(
					`/admin/services/registrations/${deptId}/reject`,
					token + '',
					{ reason: reason || '가입이 반려되었습니다.' }
				)
				if (res?.result?.success) {
					message.success(res.result.message || '학원 가입이 반려되었습니다.')
					await mutate()
				} else {
					message.error(res?.message || '반려 처리에 실패했습니다.')
				}
			} catch (err: any) {
				message.error(err?.message || '반려 요청에 실패했습니다.')
			} finally {
				setPendingRejectDeptId(null)
			}
		},
		[token, mutate]
	)

	return (
		<PageWrapper>
			<Card>
				<Header>
					<div>
						<h2>서비스 관리</h2>
						<p>회원별 GoMStem 및 WANT 서비스 이용 가능 여부를 제어합니다.</p>
					</div>
					<span className="summary">
						총 회원: <strong>{total.toLocaleString()}</strong>명
					</span>
				</Header>
				<SearchForm onSubmit={handleSearchSubmit}>
					<input
						value={searchInput}
						onChange={event => setSearchInput(event.target.value)}
						placeholder="학원명, 회원 이름 또는 이메일을 입력하세요"
					/>
					<select
						value={typeInput}
						onChange={event => setTypeInput(event.target.value)}
						style={{ width: 160 }}
						aria-label="회원 타입 선택"
					>
						<option value="">전체 타입</option>
						<option value="1">1: 원장</option>
						<option value="2">2: 강사</option>
						<option value="3">3: 학생</option>
						<option value="4">4: 게스트</option>
						<option value="5">5: 오답노트</option>
					</select>
					<select
						value={groupIdInput}
						onChange={event => setGroupIdInput(event.target.value)}
						style={{ width: 180 }}
						aria-label="그룹 ID 선택"
					>
						<option value="">전체 그룹</option>
						<option value="17">17: 마스터관리자</option>
						<option value="22">22: 관리자</option>
						<option value="24">24: 강사</option>
						<option value="25">25: 학생</option>
					</select>
					<select
						value={approvalFilter}
						onChange={event => {
							setApprovalFilter(event.target.value as 'all' | 'pending')
							setOffset(0)
						}}
						style={{ width: 200 }}
						aria-label="승인 상태 필터"
					>
						<option value="all">전체 학원</option>
						<option value="pending">신규 회원가입 학원만</option>
					</select>
					<button type="submit" disabled={isLoading}>
						검색
					</button>
					<button type="button" onClick={handleClearSearch} disabled={isLoading && !activeSearch && !activeType && !activeGroupId && approvalFilter === 'all'}>
						초기화
					</button>
				</SearchForm>
				{error && <StatusBadge $active={false}>서비스 목록을 불러오지 못했습니다.</StatusBadge>}
			</Card>

			<TableCard>
				<TableWrapper>
					{isLoading ? (
						<EmptyState>서비스 정보를 불러오는 중입니다...</EmptyState>
					) : items.length === 0 ? (
						<EmptyState>조건에 일치하는 회원이 없습니다.</EmptyState>
					) : (
						<UsersTable>
							<thead>
								<tr>
									<th style={{ width: '10%' }}>학원</th>
									<th style={{ width: '16%' }}>회원명</th>
									<th style={{ width: '16%' }}>아이디</th>
									<th style={{ width: '10%' }}>승인상태</th>
									<th style={{ width: '12%' }}>GOMS 서비스</th>
									<th style={{ width: '12%' }}>WANT 서비스</th>
									<th style={{ width: '14%' }}>상태</th>
									<th style={{ width: '10%' }}>액션</th>
								</tr>
							</thead>
							<tbody>
								{items.map(user => (
									<tr key={user.id}>
										<td>{user.deptName || '-'}</td>
										<td>
											<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
												<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
													{user.name} {user.groupName && <span style={{ fontSize: 12, color: '#FF4D4F' }}>{user.groupName}</span>}
												</div>
												<StatusBadge $active={user.type !== 5}>
													{user.type === 1 && (<span style={{ fontSize: 12, color: '#444' }}>원장</span>)}
													{user.type === 2 && (<span style={{ fontSize: 12, color: '#444' }}>강사</span>)}
													{user.type === 3 && (<span style={{ fontSize: 12, color: '#444' }}>학생</span>)}
													{user.type === 4 && (<span style={{ fontSize: 12, color: '#444' }}>게스트</span>)}
													{user.type === 5 && (<span style={{ fontSize: 12, color: '#666' }}>오답노트</span>)}
												</StatusBadge>
											</div>
										</td>
										<td>{user.email || '-'}</td>
										<td>
											{user.approvalStatus === 'approved' && <StatusBadge $active>승인</StatusBadge>}
											{(user.approvalStatus === 'pending' || user.approvalStatus === 'none') && (
												<StatusBadge $active={false}>신규 대기</StatusBadge>
											)}
											{user.approvalStatus === 'rejected' && <StatusBadge $active={false}>반려</StatusBadge>}
											{!user.approvalStatus && '-'}
										</td>
										<td>{renderServiceSwitch(user.id, 'goms', !!user.services?.goms)}</td>
										<td>{renderServiceSwitch(user.id, 'want', !!user.services?.want)}</td>
										<td>
											<StatusBadge $active={hasActiveService(user.services)}>
												{hasActiveService(user.services) ? '서비스 사용 중' : '모든 서비스 비활성'}
											</StatusBadge>
										</td>
										<td>
											{user.deptId && isPendingApproval(user.approvalStatus) && (
												<div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
													<button
														type="button"
														onClick={() => handleApproveAcademy(user.deptId!)}
														disabled={!!pendingApproveDeptId || !!pendingRejectDeptId}
														style={{
															padding: '4px 10px',
															fontSize: 12,
															backgroundColor: '#52c41a',
															color: '#fff',
															border: 'none',
															borderRadius: 4,
															cursor: pendingApproveDeptId ? 'wait' : 'pointer',
														}}
													>
														{pendingApproveDeptId === user.deptId ? '처리 중...' : '승인'}
													</button>
													<button
														type="button"
														onClick={() => handleRejectAcademy(user.deptId!)}
														disabled={!!pendingApproveDeptId || !!pendingRejectDeptId}
														style={{
															padding: '4px 10px',
															fontSize: 12,
															backgroundColor: '#ff4d4f',
															color: '#fff',
															border: 'none',
															borderRadius: 4,
															cursor: pendingRejectDeptId ? 'wait' : 'pointer',
														}}
													>
														{pendingRejectDeptId === user.deptId ? '처리 중...' : '거절'}
													</button>
												</div>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</UsersTable>
					)}
				</TableWrapper>
				<Footer>
					<div className="page-info">
						{total > 0 ? (
							<>
								페이지 {currentPage}/{totalPages} · 총 {total.toLocaleString()}명
								{isValidating && ' (동기화 중)'}
							</>
						) : (
							<>회원 정보가 없습니다.</>
						)}
					</div>
					<PaginationControls>
						<button type="button" onClick={goToPreviousPage} disabled={isPaginatingBackwardDisabled || isLoading}>
							이전
						</button>
						<button type="button" onClick={goToNextPage} disabled={isPaginatingForwardDisabled || isLoading}>
							다음
						</button>
					</PaginationControls>
				</Footer>
			</TableCard>
		</PageWrapper>
	)
}

export default React.memo(ServiceManagement)
